/**
 * Módulos de negocio del aplicativo (espejo de AviarBack/src/utils/modulos.ts).
 *
 * El módulo del cliente decide a dónde cae el usuario al iniciar sesión y qué
 * rutas puede abrir. Un cliente sin `modulos` configurado es de POSTURA, para
 * que los clientes que ya están en producción no cambien de comportamiento.
 *
 * Esto es navegación, no seguridad: el bloqueo real vive en la policy
 * `global::validate-modulo-cliente` del backend.
 */

export const MODULOS = ['postura', 'engorde'] as const

export type ModuloId = (typeof MODULOS)[number]

export const MODULO_POR_DEFECTO: ModuloId = 'postura'

export const MODULO_LABEL: Record<ModuloId, string> = {
  postura: 'Aves de postura',
  engorde: 'Pollos de engorde',
}

export const MODULO_ICONO: Record<ModuloId, string> = {
  postura: 'mdi-egg-outline',
  engorde: 'mdi-food-drumstick-outline',
}

/**
 * Tema por defecto de cada módulo (espejo del lifecycle de cliente en el backend).
 * Un cliente con las dos líneas cambia de tema al conmutar de módulo, salvo que
 * alguien le haya puesto a mano un tema que no sea ninguno de estos dos.
 */
export const MODULO_TEMA: Record<ModuloId, string> = {
  postura: 'verde-avicola',
  engorde: 'azul-corporativo',
}

export const TEMAS_AUTOMATICOS: ReadonlySet<string> = new Set(Object.values(MODULO_TEMA))

/** Ruta a la que se entra al iniciar sesión en cada módulo. */
export const MODULO_RUTA_INICIO: Record<ModuloId, string> = {
  postura: '/dashboard',
  engorde: '/engorde',
}

const normalizar = (valor: unknown): string => String(valor ?? '').toLowerCase().trim()

export const esModuloValido = (valor: unknown): valor is ModuloId =>
  (MODULOS as readonly string[]).includes(normalizar(valor))

/** Normaliza lo que llegue del backend. Nunca devuelve una lista vacía. */
export function normalizarModulos(crudo: unknown): ModuloId[] {
  const lista = Array.isArray(crudo)
    ? (crudo.filter(esModuloValido).map(normalizar) as ModuloId[])
    : []

  return lista.length > 0 ? Array.from(new Set(lista)) : [MODULO_POR_DEFECTO]
}

/** Cae al primer módulo habilitado si el preferido no lo está. */
export function resolverModuloPorDefecto(
  habilitados: ModuloId[],
  preferido: unknown,
): ModuloId {
  const normalizado = normalizar(preferido) as ModuloId
  return habilitados.includes(normalizado) ? normalizado : habilitados[0]
}

export const rutaInicioDeModulo = (modulo: ModuloId): string => MODULO_RUTA_INICIO[modulo]

/**
 * Kilos por bulto de alimento cuando el cliente no ha configurado el suyo.
 *
 * No todos los clientes empacan igual (hay bultos de 40 kg, de 25 kg, y clientes
 * que llevan el inventario directo en kilos y ponen 1). El valor real vive en
 * `cliente.KilosPorBultoAlimento`; esto es solo el respaldo.
 */
export const KILOS_POR_BULTO_POR_DEFECTO = 40

/** Normaliza el valor guardado en el cliente. Descarta ceros y negativos. */
export function normalizarKilosPorBulto(valor: unknown): number {
  const kilos = Number(valor)
  return Number.isFinite(kilos) && kilos > 0 ? kilos : KILOS_POR_BULTO_POR_DEFECTO
}

/**
 * Ajuste opcional del cliente: vacío, cero o basura se quedan en null.
 *
 * El vacío NO puede volverse 0: estos coeficientes los fija cada integrador, y
 * un 0 guardado haría que el indicador se calculara con un ajuste nulo en vez
 * de ocultarse, que es lo que debe pasar mientras nadie lo haya definido.
 */
export function numeroPositivoONull(valor: unknown): number | null {
  if (valor === null || valor === undefined || valor === '') return null
  const n = Number(valor)
  return Number.isFinite(n) && n > 0 ? n : null
}
